// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { useContext, createContext, useState, MouseEventHandler, useEffect } from 'react'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import { InteractionType, PublicClientApplication } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'

import { getUser, searchDrive, getRecentFilesFromDrive } from './GraphService'
import { getDrive } from './GraphService'

import config from './Config'

export interface AppUser {
    displayName?: string
    id?: string
    email?: string
    avatar?: string
    timeZone?: string
    timeFormat?: string
    files?: Array<any>
}

export interface FilesInterface {
    data?: string
}

export interface AppError {
    message: string
    debug?: string
}

type AppContext = {
    user?: AppUser
    error?: AppError
    signIn?: MouseEventHandler<HTMLElement>
    signOut?: MouseEventHandler<HTMLElement>
    displayError?: Function
    clearError?: Function
    authProvider?: AuthCodeMSALBrowserAuthenticationProvider
    files?: FilesInterface
    searchFiles?: Function
    searchedFiles?: Array<any>
    getRecentFiles?: Function
    recentFiles?: Array<any>
}

const appContext = createContext<AppContext>({
    user: undefined,
    error: undefined,
    signIn: undefined,
    signOut: undefined,
    displayError: undefined,
    clearError: undefined,
    authProvider: undefined,
    files: undefined,
    searchFiles: undefined,
    searchedFiles: undefined,
    recentFiles: undefined,
    getRecentFiles: undefined,
})

export function useAppContext(): AppContext {
    return useContext(appContext)
}

interface ProvideAppContextProps {
    children: React.ReactNode
}

export default function ProvideAppContext({ children }: ProvideAppContextProps) {
    const auth = useProvideAppContext()
    return <appContext.Provider value={auth}>{children}</appContext.Provider>
}

function useProvideAppContext() {
    const msal = useMsal()
    const [user, setUser] = useState<AppUser | undefined>(undefined)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [searchedFiles, setSearchedFiles] = useState(undefined)
    const [recentFiles, setRecentFiles] = useState(undefined)

    const displayError = (message: string, debug?: string) => {
        setError({ message, debug })
    }

    const clearError = () => {
        setError(undefined)
    }

    const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(msal.instance as PublicClientApplication, {
        account: msal.instance.getActiveAccount()!,
        scopes: config.scopes,
        interactionType: InteractionType.Popup,
    })

    useEffect(() => {
        const checkUser = async () => {
            if (!user) {
                try {
                    // Check if user is already signed in
                    const account = msal.instance.getActiveAccount()
                    if (account) {
                        const drive = await getDrive(authProvider)
                        const user = await getUser(authProvider)
                        setUser({
                            id: user.id || '',
                            displayName: user.displayName || '',
                            email: user.mail || user.userPrincipalName || '',
                            timeFormat: user.mailboxSettings?.timeFormat || 'h:mm a',
                            timeZone: user.mailboxSettings?.timeZone || 'UTC',
                            files: drive.value,
                        })
                        await getRecentFiles()
                    }
                } catch (err) {
                    displayError(err.message)
                }
            }
        }
        checkUser()
    })

    const signIn = async () => {
        await msal.instance.loginPopup({
            scopes: config.scopes,
            prompt: 'select_account',
        })

        const user = await getUser(authProvider)
        const drive = await getDrive(authProvider)

        setUser({
            id: user.id || '',
            displayName: user.displayName || '',
            email: user.mail || user.userPrincipalName || '',
            timeFormat: user.mailboxSettings?.timeFormat || '',
            timeZone: user.mailboxSettings?.timeZone || 'UTC',
            files: drive.value,
        })
    }

    const signOut = async () => {
        await msal.instance.logoutPopup()
        setUser(undefined)
    }

    const searchFiles = async (query: String) => {
        const x = await searchDrive(authProvider, query)
        setSearchedFiles(x.value)
    }

    const getRecentFiles = async () => {
        const x = await getRecentFilesFromDrive(authProvider)
        setRecentFiles(x.value.slice(0, 10))
    }

    return {
        user,
        error,
        signIn,
        signOut,
        displayError,
        clearError,
        authProvider,
        searchFiles,
        searchedFiles,
        getRecentFiles,
        recentFiles,
    }
}
